<template>
  <Modal
    ref="modal"
    class="LicenseAssignDialog"
    :heading="messages.license.licenseGrant"
    :size="
      isSmallPixel ? (isExtraSmallPixel ? 'extraSmall' : 'small') : 'medium'
    "
    @closeModal="onClickModalClose"
  >
    <div class="LicenseAssignDialog__layout">
      <div class="LicenseAssignDialog__inner">
        <SearchForm
          id="search"
          class="LicenseAssignDialog__searchForm"
          name="search"
          :placeholder="messages.common.searchPlaceholder"
          :value="searchWord"
          @inputWord="searchWord = $event"
        />
      </div>
      <div class="LicenseAssignDialog__info">
        <p>{{ messages.license.info.licenseAssignInfo1 }}</p>
        <p class="LicenseAssignDialog__info">
          {{ messages.license.info.licenseAssignInfo2 }}
        </p>
      </div>
      <div class="LicenseAssignDialog__userListWrapper">
        <UserList
          class="LicenseAssignDialog__userList"
          :displayUsers="displayUsersValue"
          :noDataText="noDataText"
          :isListLoading="isListLoading"
          :headList="headList"
          :fromDialog="true"
          @onCheckItem="onCheckItem"
        />
      </div>

      <div class="LicenseAssignDialog__paginationWrapper">
        <div class="LicenseAssignDialog__info">
          <span> {{ countDisplayLabel }}</span>
        </div>
        <PaginationBar
          class="LicenseAssignDialog__pagination"
          :currentPage="currentPage"
          :maxDisplayPages="maxDisplayPages"
          :itemsPerPage="MAX_DISPLAY_NUM"
          :total="totalCount"
          @onClick:pageNumber="movePage"
        />
      </div>

      <div class="LicenseAssignDialog__modalInner">
        <div class="LicenseAssignDialog__modalButtonDiv">
          <Button
            class="LicenseAssignDialog__modalButton"
            tag="button"
            dynamics="secondary"
            :label="messages.common.cancel"
            @click="onClickModalClose"
          />
          <Button
            class="LicenseAssignDialog__modalButton"
            tag="button"
            dynamics="primary"
            :label="messages.common.grant"
            :isDisabled="isButtonDisabled"
            @click="onClickLicenseAssign"
          />
        </div>
      </div>
    </div>
  </Modal>
</template>

<script lang="ts">
export default {
  name: 'LicenseAssignDialog',
};
</script>

<script lang="ts" setup>
import Button from '@spectee/components/Atoms/Button/index.vue';
import UserList from '@spectee/components/Organisms/UserList/index.vue';
import Modal from '@spectee/components/Organisms/Modal/index.vue';
import { ref, computed, watch } from 'vue';
import { userStatus } from '@spectee/ts/userStatus';
import SearchForm from '@spectee/components/Atoms/SearchForm/index.vue';
import PaginationBar from '@spectee/components/Molecules/PaginationBar/index.vue';
import { useWindowWidth } from '@spectee/composables/useWindowWidth';
import messages from '../../../../messages.json';
import { UserItem } from '@spectee/types/atoms';

interface Props {
  assignableCount: number;
  displayUsers: UserItem[];
  isListLoading: boolean;
  isThChecked?: boolean;
}

const props = withDefaults(defineProps<Props>(), {});

const { isSmallPixel, isExtraSmallPixel } = useWindowWidth();
const MAX_DISPLAY_NUM = 50;
const maxDisplayPages = 5;
const currentPage = ref(1);

const searchWord = ref('');

// フィルター後のユーザー一覧
const filteredUser = computed(() => {
  if (searchWord.value) {
    const regex = new RegExp(searchWord.value, 'i');
    return displayUsersRef.value.filter(
      userItem =>
        regex.test(userItem.user.name) ||
        regex.test(userItem.user.email) ||
        regex.test(userItem.user.authority?.displayName)
    );
  }
  return displayUsersRef.value;
});

const noDataText = ref(messages.license.info.noMatchingUsers);

const totalCount = computed(() => {
  return filteredUser.value.length;
});

const start = computed(() => (currentPage.value - 1) * MAX_DISPLAY_NUM);
const end = computed(() =>
  Math.min(start.value + MAX_DISPLAY_NUM, totalCount.value)
);

const displayUsersValue = computed(() =>
  filteredUser.value.slice(start.value, end.value)
);

const movePage = (pageNumber: number) => {
  currentPage.value = pageNumber;
};

const assignableCountRef = computed(() => {
  return props.assignableCount - checkedItemsCount.value;
});

const displayUsersRef = ref<UserItem[]>(props.displayUsers);

const checkedItemsCount = computed(() => {
  return displayUsersRef.value.filter(user => user.isChecked).length;
});

const userIdsToAddLicense = computed(() => {
  return displayUsersRef.value
    .filter(user => user.isChecked)
    .map(user => user.user.auth0UserId);
});

const isButtonDisabled = computed(() => {
  return checkedItemsCount.value === 0 ? true : false;
});

const headList = [
  { key: 'name', label: messages.common.headList.name },
  { key: 'displayName', label: messages.common.headList.displayName },
  { key: 'email', label: messages.common.headList.email },
  { key: 'groupName', label: messages.common.headList.groupName },
  { key: 'authority', label: messages.common.headList.authority },
];

const emit = defineEmits<{
  (e: 'onClickModalClose'): void;
  (e: 'onClickLicenseAssign', params: string[]): void;
}>();

const onClickModalClose = () => {
  emit('onClickModalClose');
};

const onClickLicenseAssign = () => {
  emit('onClickLicenseAssign', userIdsToAddLicense.value);
};

const onCheckItem = (event: Event, checkedUser: UserItem) => {
  const { target } = event;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }

  // Update the checked state of the clicked user
  displayUsersRef.value.forEach(user => {
    if (
      (checkedUser.status === user.status &&
        checkedUser.status === userStatus.active &&
        user.user.auth0UserId === checkedUser.user.auth0UserId) ||
      (checkedUser.status === user.status &&
        checkedUser.status === userStatus.invited &&
        user.user.email === checkedUser.user.email) // Need to remove the OR condition
    ) {
      user.isChecked = target.checked;
    }
  });

  const limitReached = checkedItemsCount.value >= props.assignableCount;

  // To Update isMinus
  displayUsersRef.value.forEach(user => {
    const isCurrentChecked = user.isChecked;

    // If limit is reached and this user is NOT already checked, mark as minus
    user.isMinus = limitReached && !isCurrentChecked;
  });
};

watch(
  () => props.displayUsers,
  async newVal => {
    if (newVal) {
      displayUsersRef.value = newVal;
    }
  }
);

const countDisplayLabel = computed(() =>
  messages.license.remainingLicense.replace(
    '{{count}}',
    assignableCountRef.value.toString()
  )
);
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_forward.scss' as *;
.LicenseAssignDialog {
  &__layout {
    display: grid;
    grid-template-rows: auto // SearchForm
      1fr // UserList takes remaining height
      auto // Pagination
      auto; // Buttons
    gap: $service-unit * 2;
    max-height: 80vh;
    // min-height: 60vh;
  }

  &__userListWrapper {
    overflow: auto;
    min-height: 0;
  }

  &__userList {
    height: 100%;
    display: grid;
    grid-auto-rows: min-content;
    gap: $service-unit;
  }

  &__modalInner {
    padding-top: $service-unit;
    text-align: center;
  }

  &__modalButtonDiv {
    display: flex;
    justify-content: center;
    gap: 12px;
  }

  &__paginationWrapper {
    display: flex;
    flex-direction: column;
    gap: $service-unit;
  }

  &__pagination {
    display: flex;
    grid-template-columns: none;
    gap: $service-unit * 10;
  }

  &__info {
    font-size: $service-font-size-mp;
    color: $color-gray-60;
  }

  &__searchForm {
    margin-top: $service-unit * 3;
    width: 50%;
    max-width: $service-unit * 100;
  }

  @media (max-width: 425px) {
    &__pagination {
      display: block;
    }

    &__info,
    &__modalButton {
      font-size: $service-font-size-ppp;
    }
  }
}
</style>
